import React from "react";

/**
 * Three brand-aligned logo directions for Thrive by Madsen.
 * Each component is a pure SVG with `fill="currentColor"` where possible
 * so it inherits palette via Tailwind text-{ink|bone|terracotta|forest}.
 *
 * All marks are designed to:
 *   - Survive at 32px (favicon)
 *   - Work in mono (single color print/emboss)
 *   - Pair with the editorial wordmark already on the site
 */

/* ─────────────────────────────────────────────
 * DIRECTION A — Editorial Wordmark + Accent
 * Concept: Pure typography. Playfair italic "Thrive" with a
 * terracotta micro-dot replacing the dot on the "i", and a
 * tight "by Madsen" overline below. Most premium, most distinctive.
 * ───────────────────────────────────────────── */
export const LogoEditorial = ({ size = 240, accent = "#C85A40", color = "currentColor" }) => (
  <svg
    viewBox="0 0 480 200"
    width={size}
    height={(size / 480) * 200}
    role="img"
    aria-label="Thrive by Madsen"
  >
    {/* Thrive — Playfair Display italic */}
    <text
      x="240"
      y="120"
      textAnchor="middle"
      fontFamily="'Playfair Display', serif"
      fontStyle="italic"
      fontWeight="700"
      fontSize="140"
      fill={color}
      letterSpacing="-3"
    >
      Thrive
    </text>
    {/* Accent dot — replaces the dot on the 'i' (positioned by eye) */}
    <circle cx="287" cy="48" r="9" fill={accent} />
    {/* by MADSEN overline */}
    <text
      x="240"
      y="172"
      textAnchor="middle"
      fontFamily="'Outfit', sans-serif"
      fontWeight="800"
      fontSize="20"
      letterSpacing="6"
      fill={color}
    >
      BY MADSEN
    </text>
    {/* Hairline rule */}
    <line x1="155" y1="145" x2="195" y2="145" stroke={accent} strokeWidth="2" />
    <line x1="285" y1="145" x2="325" y2="145" stroke={accent} strokeWidth="2" />
  </svg>
);

/* ─────────────────────────────────────────────
 * DIRECTION B — TM Monogram Cartouche
 * Concept: A sharp, architectural "T over M" monogram inside a
 * thin-rule square. Pairs with the wordmark to the right.
 * Scales to 32px (the cartouche reads even as a single block).
 * ───────────────────────────────────────────── */
export const LogoMonogram = ({ size = 280, accent = "#C85A40", color = "currentColor" }) => (
  <svg
    viewBox="0 0 600 200"
    width={size}
    height={(size / 600) * 200}
    role="img"
    aria-label="Thrive by Madsen"
  >
    {/* Cartouche square */}
    <rect
      x="10"
      y="20"
      width="160"
      height="160"
      fill="none"
      stroke={color}
      strokeWidth="3"
    />
    {/* T (sharp serif top, slab vertical) */}
    <path
      d="M 40 50 L 140 50 L 140 64 L 96 64 L 96 150 L 84 150 L 84 64 L 40 64 Z"
      fill={color}
    />
    {/* M underline — a single terracotta horizontal slab below the T */}
    <rect x="40" y="158" width="100" height="6" fill={accent} />
    {/* Tiny 'm' tag bottom-right of cartouche */}
    <text
      x="158"
      y="172"
      textAnchor="end"
      fontFamily="'Outfit', sans-serif"
      fontWeight="900"
      fontSize="16"
      letterSpacing="2"
      fill={accent}
    >
      m
    </text>

    {/* Wordmark right side */}
    <text
      x="200"
      y="108"
      fontFamily="'Outfit', sans-serif"
      fontWeight="900"
      fontSize="62"
      letterSpacing="-1"
      fill={color}
    >
      THRIVE
    </text>
    <text
      x="200"
      y="148"
      fontFamily="'Outfit', sans-serif"
      fontWeight="600"
      fontSize="16"
      letterSpacing="8"
      fill={color}
      opacity="0.7"
    >
      BY MADSEN
    </text>
  </svg>
);

/* Compact monogram-only mark (favicon / bottle cap) */
export const MonogramMark = ({ size = 64, accent = "#C85A40", color = "currentColor" }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} role="img" aria-label="Thrive by Madsen">
    <rect x="10" y="10" width="180" height="180" fill="none" stroke={color} strokeWidth="4" />
    <path
      d="M 40 50 L 160 50 L 160 68 L 109 68 L 109 160 L 91 160 L 91 68 L 40 68 Z"
      fill={color}
    />
    <rect x="40" y="170" width="120" height="8" fill={accent} />
  </svg>
);

/* ─────────────────────────────────────────────
 * DIRECTION C — Mineral Crystal Mark
 * Concept: An abstract faceted crystal (read as: a salt crystal,
 * a mineral fragment) tied to electrolytes/grain. Pairs cleanly
 * with the wordmark. Works in single color.
 * ───────────────────────────────────────────── */
export const LogoCrystal = ({ size = 280, accent = "#C85A40", color = "currentColor" }) => (
  <svg
    viewBox="0 0 600 200"
    width={size}
    height={(size / 600) * 200}
    role="img"
    aria-label="Thrive by Madsen"
  >
    {/* Faceted crystal — built from triangles, terracotta core + ink facets */}
    <g transform="translate(20,30)">
      {/* Outer diamond outline */}
      <polygon
        points="70,0 140,55 110,140 30,140 0,55"
        fill="none"
        stroke={color}
        strokeWidth="3"
      />
      {/* Inner facets */}
      <polygon points="70,0 110,140 30,140" fill={accent} opacity="0.9" />
      <polygon points="70,0 140,55 110,140" fill={color} opacity="1" />
      <polygon points="70,0 0,55 30,140" fill={color} opacity="0.85" />
      {/* Highlight glint */}
      <line x1="70" y1="0" x2="70" y2="140" stroke="#F5F5F0" strokeWidth="1.5" opacity="0.6" />
    </g>

    {/* Wordmark */}
    <text
      x="200"
      y="100"
      fontFamily="'Playfair Display', serif"
      fontStyle="italic"
      fontWeight="700"
      fontSize="68"
      fill={color}
      letterSpacing="-2"
    >
      Thrive
    </text>
    <text
      x="200"
      y="138"
      fontFamily="'Outfit', sans-serif"
      fontWeight="800"
      fontSize="16"
      letterSpacing="8"
      fill={color}
      opacity="0.75"
    >
      BY MADSEN
    </text>
  </svg>
);

/* Compact crystal-only mark (favicon / bottle cap) */
export const CrystalMark = ({ size = 64, accent = "#C85A40", color = "currentColor" }) => (
  <svg viewBox="0 0 160 160" width={size} height={size} role="img" aria-label="Thrive by Madsen">
    <g transform="translate(10,10)">
      <polygon points="70,0 140,55 110,140 30,140 0,55" fill="none" stroke={color} strokeWidth="4" />
      <polygon points="70,0 110,140 30,140" fill={accent} />
      <polygon points="70,0 140,55 110,140" fill={color} />
      <polygon points="70,0 0,55 30,140" fill={color} opacity="0.85" />
    </g>
  </svg>
);

/* Editorial-only "T*" favicon variant for Direction A */
export const EditorialMark = ({ size = 64, accent = "#C85A40", color = "currentColor" }) => (
  <svg viewBox="0 0 160 160" width={size} height={size} role="img" aria-label="Thrive by Madsen">
    <text
      x="80"
      y="120"
      textAnchor="middle"
      fontFamily="'Playfair Display', serif"
      fontStyle="italic"
      fontWeight="700"
      fontSize="160"
      fill={color}
    >
      T
    </text>
    <circle cx="118" cy="48" r="10" fill={accent} />
  </svg>
);
